import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin, Users, Tag, Shield, Check, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useEvents } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import DiscountDrawer from '@/components/utils/DiscountDrawer';
import {
  createBooking,
  getCategoryById,
  cancelBookingById,
  validateDiscountCode,
} from "@/api/apiUtils";
import { useRazorpay, verifyPayment } from '@/services/paymentService';
import { toast } from 'react-hot-toast';
import { mergeDateTime } from '@/utils/formatdate';
import { cleanLocationText } from '@/utils/cleanLocationText';
import { track } from '@/lib/analytics';
import { friendlyError } from '@/lib/apiError';

const BookingPage = () => {
  const { id: eventId } = useParams();

  useEffect(() => {
    track('checkout_started', { serviceId: eventId });
  }, [eventId]);
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuth();
  const { setBookingDetails } = useEvents();
  const location = useLocation();
  const { initiatePayment, isPaymentLoading } = useRazorpay();

  // Get data from navigation state
  const {
    event,
    selectedTickets = [],
    selectedDate,
    selectedTime,
    seatIds,
    holdToken,
    holdExpiresAt,
  } = location.state || {};

  const [bookingStatus, setBookingStatus] = useState('idle'); // 'idle' | 'processing' | 'success' | 'error'
  const [bookingId, setBookingId] = useState(null);

  // Discount state - using array pattern for DiscountDrawer
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [platformFee, setPlatformFee] = useState(() => Number(location.state?.event?.platformFee) || 0);

  // Redirect back if no data is available
  useEffect(() => {
    if (!event || !selectedTickets || selectedTickets.length === 0) {
      navigate(`/tickets/${eventId}`);
    }
  }, [event, selectedTickets, eventId, navigate]);

  // Helper function to calculate total discount amount from all applied discounts
  const calculateTotalDiscount = () => {
    return appliedDiscounts.reduce((total, discount) => {
      if (discount.type === 'fixed' || discount.discountAmount) {
        return total + (discount.discountAmount || 0);
      }
      const percentSavings = (subtotal * (discount.discountPercent || 0)) / 100;
      if (discount.maxDiscountAmount && percentSavings > discount.maxDiscountAmount) {
        return total + discount.maxDiscountAmount;
      }
      return total + percentSavings;
    }, 0);
  };

  // Get the first applied discount code for booking payload
  const getAppliedDiscountCode = () => {
    return appliedDiscounts.length > 0 ? appliedDiscounts[0].code : undefined;
  };

  // Get cities from applied discount for booking payload
  const getAppliedDiscountCities = () => {
    return appliedDiscounts.length > 0 ? (appliedDiscounts[0].cities || ['Bikaner']) : ['Bikaner'];
  };

  // Fetch platform fee from category
  useEffect(() => {
    const fetchPlatformFee = async () => {
      if (event?.categoryId) {
        try {
          const response = await getCategoryById(event.categoryId);
          if (response?.success && response?.data?.platformFee) {
            setPlatformFee(Number(response.data.platformFee));
          }
        } catch (error) {
          console.error('Error fetching platform fee:', error);
        }
      }
    };
    fetchPlatformFee();
  }, [event?.categoryId]);

  useEffect(() => {
    if (!event) {
      navigate('/');
    }
  }, [event, navigate]);

  if (!event) return null;

  // Calculate totals from selected tickets
  const subtotal = selectedTickets.reduce((sum, ticket) => sum + ticket.totalPrice, 0);

  // Calculate discount from applied discounts
  const promoDiscountAmount = appliedDiscounts.reduce((total, discount) => {
    if (discount.discountAmount) {
      return total + Number(discount.discountAmount || 0);
    }
    if (discount.savings) {
      return total + Number(discount.savings || 0);
    }
    if (discount.discountPercent || discount.value) {
      const percent = discount.discountPercent || discount.value || 0;
      const percentSavings = (subtotal * Number(percent)) / 100;
      if (discount.maxDiscountAmount && percentSavings > discount.maxDiscountAmount) {
        return total + Number(discount.maxDiscountAmount);
      }
      return total + percentSavings;
    }
    return total;
  }, 0);

  const finalTotal = subtotal - promoDiscountAmount + platformFee;

  const updateBookingStatus = async (bookingId, status) => {
    try {
      // TODO: Implement actual API call to update booking status
      console.log(`Updating booking ${bookingId} status to:`, status);
      return true;
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status');
      return false;
    }
  };

  const handlePaymentFailure = async (error) => {
    console.error('Payment failed:', error);
    setBookingStatus('error');

    // Update booking status to failed
    if (bookingId) {
      await updateBookingStatus(bookingId, 'payment_failed');
    }

    toast.error('Payment failed. Please try again.');
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error('Please login to book tickets');
      openAuthModal();
      return;
    }

    setBookingStatus('processing');
    setIsProcessing(true);

    try {
      // Convert date from yyyy-MM-dd to dd-MM-yyyy format for mergeDateTime
      const convertDateFormat = (dateStr) => {
        if (!dateStr) throw new Error('Date is required');
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      };

      // Get the event time (first time slot if multiple)
      const eventTime = selectedTime || '10:00 AM';

      // Use mergeDateTime utility to format scheduledTime
      // Format: "YYYY-MM-DD HH:mm:ss"
      const scheduledTime = mergeDateTime(
        convertDateFormat(selectedDate),
        eventTime
      );

      console.log('Formatted scheduledTime:', scheduledTime);

      // Build services array with proper variant structure
      const services = [{
        providerServiceId: event.providerServiceId || event.id,
        quantity: 1,
        variants: selectedTickets.map(ticket => ({
          spServiceVariantId: ticket.id,
          qty: ticket.quantity
        }))
      }];

      // Build booking payload per API requirements
      const bookingData = {
        categoryId: event.categoryId,
        providerId: event.providerId,
        bookingType: "store",
        scheduledTime,
        notes: "",
        kcoinAmountToUse: 0,
        paymentMode: "online",
        services,
        attributes: [],
        discountCode: getAppliedDiscountCode(),
        cities: getAppliedDiscountCities(),
        // Seat selection (events) — only present when user came from /seat-selection
        ...(Array.isArray(seatIds) && seatIds.length > 0 && holdToken
          ? { seatIds, holdToken }
          : {}),
      };

      console.log('Creating booking with payload:', bookingData);

      const bookingResponse = await createBooking(bookingData);
      console.log('Booking response:', bookingResponse);

      // Extract booking ID - handle different response structures
      const createdBookingId = bookingResponse?.data?.booking?.id ||
        bookingResponse?.data?.id ||
        bookingResponse?.id;
      const payment = bookingResponse?.data?.payment || bookingResponse?.payment;

      console.log('Created booking ID:', createdBookingId);
      console.log('Payment object:', payment);

      if (!createdBookingId) {
        throw new Error('Booking was created but ID was not returned');
      }

      setBookingId(createdBookingId);

      // 2. Initialize Razorpay payment
      if (!payment) {
        throw new Error('Payment order was not created');
      }

      const options = {
        handler: async function (response) {
          try {
            // Verify payment with your backend
            const verification = await verifyPayment(response);

            if (verification.success) {
              // Set booking details in context
              setBookingDetails({
                bookingId: createdBookingId,
                bookingCode: bookingResponse?.data?.bookingCode || createdBookingId,
                event: {
                  ...event,
                  date: selectedDate,
                  time: selectedTime
                },
                tickets: selectedTickets,
                subtotal: Number(subtotal || 0).toFixed(2),
                discount: Number(promoDiscountAmount || 0).toFixed(2),
                total: Number(finalTotal || 0).toFixed(2),
                promoCode: getAppliedDiscountCode(),
              });

              setBookingStatus('success');
              toast.success('Booking confirmed!');

              // Navigate to success page
              navigate('/booking/confirmation', {
                state: {
                  bookingId: createdBookingId,
                  bookingCode: bookingResponse?.data?.bookingCode || createdBookingId,
                  event: {
                    ...event,
                    date: selectedDate,
                    time: selectedTime
                  },
                  tickets: selectedTickets,
                  total: Number(finalTotal || 0).toFixed(2)
                }
              });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            setBookingStatus('error');
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: async () => {
            // Handle modal dismissal (user closed the payment modal)
            try {
              await cancelBookingById(createdBookingId, { reason: 'Payment cancelled by user' });
            } catch (e) {
              console.error('Failed to cancel booking:', e);
            }
            setBookingStatus('idle');
            setIsProcessing(false);
            toast.error('Payment cancelled');
          }
        },
        onFailure: (error) => {
          handlePaymentFailure(error);
          setIsProcessing(false);
        }
      };

      // Initiate Razorpay payment with the payment order from response
      initiatePayment(payment, options);

    } catch (error) {
      console.error('Booking error:', error);
      setBookingStatus('error');
      toast.error(friendlyError(error, 'Failed to process booking'));
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="lk-container max-w-5xl flex-1 py-6 pb-24 sm:pb-10">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="lk-focus mb-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg text-sm font-medium text-ink-500 transition-colors hover:text-ink-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to event</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Booking Form */}
          <div className="space-y-6">
            {/* Secure Checkout badge */}
            <div className="lk-card flex animate-floatUp items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-ink-900">Secure Checkout</h2>
                  <p className="text-xs text-ink-500">256-bit encryption • PCI compliant</p>
                </div>
              </div>
              <span className="hidden text-xs text-ink-500 sm:inline-block">Need help? Contact support</span>
            </div>

            {/* Discount Drawer Component */}
            <div className="animate-floatUp" style={{ animationDelay: '60ms' }}>
              <DiscountDrawer
                appliedDiscounts={appliedDiscounts}
                setAppliedDiscounts={setAppliedDiscounts}
                dataToFetchDiscount={{
                  providerId: event.providerId,
                  categoryId: event.categoryId,
                  bookingAmount: subtotal,
                  serviceIds: [event.providerServiceId || event.id],
                  paymentMode: 'online'
                }}
                loading={discountLoading}
                setLoading={setDiscountLoading}
                user={user}
                openAuthModal={openAuthModal}
              />
            </div>

            {/* Payment methods removed in simplified UI. We can integrate at the gateway step. */}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            {/* The order summary holds the Pay button — never gate it on a
                JS animation (framer-motion initial opacity:0 left it blank
                whenever the animation stalled). */}
            <div className="lk-card sticky top-24 animate-floatUp p-5 sm:p-6" style={{ animationDelay: '120ms' }}>
              <h2 className="mb-5 text-xl font-bold text-ink-900">Order summary</h2>

              {/* Event Info */}
              <div className="mb-4 border-b border-ink-200/70 pb-4">
                <h3 className="mb-2 font-semibold text-ink-900">{event.title}</h3>

                <div className="space-y-2 text-sm text-ink-500">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-brand-secondary" />
                    <span>{format(new Date(selectedDate), 'EEEE, MMMM d, yyyy')} • {selectedTime}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-brand-secondary" />
                    <span>{cleanLocationText(event.venue)}</span>
                  </div>
                </div>
              </div>

              {/* Selected Tickets */}
              <div className="mb-4">
                <h4 className="mb-2 font-medium text-ink-900">Your tickets</h4>
                <div className="space-y-3">
                  {selectedTickets.map((ticket, index) => (
                    <div key={index} className="flex items-start justify-between rounded-xl bg-surface-muted p-3">
                      <div className="min-w-0 pr-2">
                        <p className="font-medium text-sm">{ticket.name} × {ticket.quantity}</p>
                        {Array.isArray(ticket.seatLabels) && ticket.seatLabels.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {ticket.seatLabels.map((label) => (
                              <span
                                key={label}
                                className="inline-block rounded border border-ink-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-ink-700"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="mt-1 text-xs text-ink-500">₹{ticket.pricePerTicket} each</p>
                      </div>
                      <span className="font-medium whitespace-nowrap">₹{ticket.totalPrice.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ticket Selection */}
              {/* Ticket selector hidden per request */}

              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-ink-200/70 pt-4">
                <div className="flex justify-between">
                  <span className="text-ink-500">Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>

                {appliedDiscounts.length > 0 && (
                  <div className="space-y-2">
                    {appliedDiscounts.map((discount) => {
                      const savings = discount.type === 'fixed' || discount.discountAmount
                        ? discount.discountAmount
                        : Math.min(
                          (subtotal * (discount.discountPercent || 0)) / 100,
                          discount.maxDiscountAmount || Infinity
                        );
                      return (
                        <div key={discount.id} className="flex justify-between text-green-600">
                          <span className="flex items-center">
                            <Tag className="w-4 h-4 mr-1" />
                            {discount.code}
                            {discount.discountPercent > 0 && ` (-${discount.discountPercent}%)`}
                          </span>
                          <span>-₹{savings.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-between text-sm text-ink-500">
                  <span>Platform fee</span>
                  <span>₹{platformFee.toLocaleString()}</span>
                </div>

                <div className="flex justify-between border-t border-ink-200/70 pt-3 text-lg font-bold text-ink-900">
                  <span>Total</span>
                  <span>₹{finalTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Book Button */}
              {/* Desktop only — phones get the sticky pay bar below, so the one
                  action on this page is never a long scroll away. */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                className="lk-btn-primary mt-6 hidden w-full lg:inline-flex"
                onClick={handleBooking}
                disabled={isProcessing || isPaymentLoading}
              >
                {(isProcessing || isPaymentLoading) ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : 'Confirm & Pay'}
              </motion.button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-ink-500">
                <Shield className="w-4 h-4" />
                <span>100% secure payment</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Phones: the pay button sat at the very bottom of the order summary,
          below the fold, while the event and ticket pages both pin their CTA.
          Same handler and disabled state as the desktop button. */}
      <div className="sticky bottom-0 z-40 border-t border-ink-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-bar lg:hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-ink-500">Total to pay</p>
            <p className="tabular text-lg font-bold leading-tight text-ink-900">
              ₹{finalTotal.toLocaleString('en-IN')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleBooking}
            disabled={isProcessing || isPaymentLoading}
            className="lk-btn-primary flex-1"
          >
            {isProcessing || isPaymentLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Processing…
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" aria-hidden="true" />
                Confirm & Pay
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
